import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { UploadZone } from '../components/UploadZone';
import {
  fetchDrawers,
  uploadPostCover,
  uploadPostResource,
  fetchDrawerDefaultResource,
  fetchPostBySlug,
  fetchPollOptionsForPost,
  adminDeletePost,
  setPostSchedule,
} from '../lib/queries';
import { adminCreateWizardPost, adminUpdateWizardPost } from '../lib/stream-queries';
import type {
  NewEventInput,
  NewAuctionInput,
  NewWizardPostInput,
} from '../lib/stream-queries';
import type { Drawer, PostButton, PostFormat } from '../lib/types';
import { WizardStepper, type StepDef } from '../components/wizard/WizardStepper';
import { MediaUploader, toWizardMedia, type MediaEntry } from '../components/wizard/MediaUploader';
import { ButtonEditor, cleanButtons } from '../components/wizard/ButtonEditor';
import { RichTextEditor } from '../components/RichText';
import { htmlToText } from '../lib/sanitizeHtml';
import { Button, Heading, Text, Input, Textarea } from '../components/ui';
import './NewPost.css';

// ---------- Closet → flow mapping -----------------------------------------
// The chosen closet (step 1) IS the type. The post's special behaviour and
// PostFormat are derived from that closet's slug - no separate "type" picker.
type ContentType = 'post' | 'redesign' | 'auction' | 'event';

/** Which behaviour a closet drives, derived from its slug. */
function closetFlow(slug: string | undefined | null): ContentType {
  switch (slug) {
    case 'sales-room': return 'auction';
    case 'redesign-challenge': return 'redesign';
    case 'monthly-zoom-qa': return 'event';
    default: return 'post';
  }
}

/** The PostFormat for a given flow. Only redesign is non-standard. */
function formatForFlow(flow: ContentType): PostFormat {
  return flow === 'redesign' ? 'redesign' : 'standard';
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function isoLocalNextHour(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  d.setDate(d.getDate() + 1);
  return toLocalInput(d);
}
function isoLocalPlus(mins: number): string {
  return toLocalInput(new Date(Date.now() + mins * 60_000));
}

const MIN_POLL_OPTIONS = 2;
const MAX_POLL_OPTIONS = 6;

// localStorage key for the in-progress new-post snapshot (create route only).
// Lets the wizard survive a refresh/navigation before the first server save,
// and restore instantly without a round-trip.
const LOCAL_DRAFT_KEY = 'cc:newpost-draft';

// How long after the last edit to push a server autosave (ms).
const AUTOSAVE_DEBOUNCE_MS = 2000;

/** Everything the local snapshot persists. Media is restored only for items
 *  already uploaded (ready) - pending uploads can't survive a reload. */
interface LocalDraftSnapshot {
  postId: string | null;
  selectedDrawerId: string | null;
  title: string;
  hideTitle: boolean;
  dek: string;
  slug: string;
  slugDirty: boolean;
  body: string;
  media: MediaEntry[];
  thumbnailUrl: string;
  resourceUrl: string;
  buttons: PostButton[];
  pollEnabled: boolean;
  pollOptions: string[];
  eventStarts: string;
  hasEventEnd: boolean;
  eventEnds: string;
  eventUrl: string;
  lotNumber: string;
  lotName: string;
  auctionStarts: string;
  auctionEnds: string;
  minBid: string;
  postageCost: string;
  parcelPreset: string;
  weightGrams: string;
  itemCategory: string;
  buyNowPrice: string;
  shippingInfo: string;
  sizeInfo: string;
  materialInfo: string;
}

export default function NewPost() {
  const { profile, loading } = useSession();
  const navigate = useNavigate();
  const toast = useToast();

  // ---------- Edit mode ----------
  // Present only on the /admin/edit/:slug route. When set, the wizard loads the
  // post, prefills every field, locks the slug, and saves via update (not create).
  const { slug: editSlug } = useParams<{ slug: string }>();
  const isEditing = Boolean(editSlug);
  const [editPostId, setEditPostId] = useState<string | null>(null);
  const [editPublished, setEditPublished] = useState(false);
  const [prefilling, setPrefilling] = useState(isEditing);
  const [prefillError, setPrefillError] = useState('');

  // ---------- Wizard position ----------
  const [stepIndex, setStepIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);

  // ---------- Step 1: closet picker ----------
  // The selected closet drives everything: its slug derives the flow/format.
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  const [selectedDrawerId, setSelectedDrawerId] = useState<string | null>(null);
  const selectedDrawer = drawers.find((d) => d.id === selectedDrawerId) ?? null;
  // Flow/format are DERIVED from the chosen closet's slug. `contentType` is kept
  // as a const so the rest of the wizard (steps plan, validation, payload)
  // continues to switch on it unchanged.
  const contentType: ContentType = closetFlow(selectedDrawer?.slug);

  // ---------- Details ----------
  const [title, setTitle] = useState('');
  const [hideTitle, setHideTitle] = useState(false);
  const [dek, setDek] = useState(''); // optional subheading (sits under the title)
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [body, setBody] = useState(''); // rich-text HTML

  // ---------- Media ----------
  const [media, setMedia] = useState<MediaEntry[]>([]);
  const [streamNotConfigured, setStreamNotConfigured] = useState(false);

  // ---------- Feed thumbnail ----------
  // Optional per-post cover shown on the closet wall / feed cards. Falls back
  // to the first media item when empty.
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [thumbUploading, setThumbUploading] = useState(false);

  // ---------- Redesign trace resource ----------
  const [resourceUrl, setResourceUrl] = useState('');
  const [resourceUploading, setResourceUploading] = useState(false);

  // ---------- Buttons ----------
  const [buttons, setButtons] = useState<PostButton[]>([]);

  // ---------- Poll ----------
  const [pollEnabled, setPollEnabled] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);

  // ---------- Zoom Q+A (event - always online) ----------
  const [eventStarts, setEventStarts] = useState(isoLocalNextHour());
  const [hasEventEnd, setHasEventEnd] = useState(false);
  const [eventEnds, setEventEnds] = useState(isoLocalPlus(60));
  const [eventUrl, setEventUrl] = useState('');

  // ---------- Auction ----------
  const [lotNumber, setLotNumber] = useState('');
  const [lotName, setLotName] = useState('');
  const [auctionStarts, setAuctionStarts] = useState(isoLocalPlus(0));
  const [auctionEnds, setAuctionEnds] = useState(isoLocalPlus(24 * 60));
  const [minBid, setMinBid] = useState('');
  // Dollar strings; '' = unset. Postage '0' means free postage (distinct from unset).
  const [postageCost, setPostageCost] = useState('');
  // Live postage (067): box preset + packed weight. Both set → members see a
  // live carrier estimate for their address and the winner is charged the
  // exact rate; either empty → flat postage above is all there is.
  const [parcelPreset, setParcelPreset] = useState('');
  const [weightGrams, setWeightGrams] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [buyNowPrice, setBuyNowPrice] = useState('');
  const [shippingInfo, setShippingInfo] = useState('');
  const [sizeInfo, setSizeInfo] = useState('');
  const [materialInfo, setMaterialInfo] = useState('');

  // ---------- Publish ----------
  const [status, setStatus] = useState<'idle' | 'publishing' | 'error'>('idle');
  const [errorText, setErrorText] = useState('');

  const publishing = status === 'publishing';

  // ---------- Scheduling (create route, Review step) ----------
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(isoLocalNextHour());

  // ---------- Autosave (create route only) ----------
  // Drafts are real post rows so they show up in admin and survive a reload.
  // A local snapshot gives instant restore; a debounced server write keeps the
  // draft row in sync once the post has a title.
  const [autosave, setAutosave] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Set when another tab publishes/discards this draft (clears the shared
  // localStorage key). We stop autosaving so we don't resurrect a deleted row
  // or un-publish a now-live post from a stale tab.
  const [draftDetached, setDraftDetached] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);       // a server write is in flight
  const dirtyRef = useRef(false);        // unsaved changes accumulated mid-write
  const restoredRef = useRef(false);     // local snapshot restore ran once
  const skipNextLocalSave = useRef(true); // don't persist the initial mount

  useEffect(() => {
    const ctrl = new AbortController();
    fetchDrawers(ctrl.signal)
      .then((d) => { if (!ctrl.signal.aborted) setDrawers(d); })
      .catch((err) => { if (!ctrl.signal.aborted && err?.name !== 'AbortError') console.error(err); });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (isEditing) return; // slug is locked in edit mode
    if (!slugDirty) setSlug(slugify(title));
  }, [title, slugDirty, isEditing]);

  // ---------- Edit-mode prefill ----------
  // Load the post (admins bypass RLS, so drafts load) + its poll options, then
  // hydrate every wizard field. Runs once when entering on the edit route.
  useEffect(() => {
    if (!isEditing || !editSlug) return;
    const ctrl = new AbortController();
    setPrefilling(true);
    setPrefillError('');
    (async () => {
      try {
        const post = await fetchPostBySlug(editSlug, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (!post) { setPrefillError('Post not found.'); setPrefilling(false); return; }
        const pollOpts = await fetchPollOptionsForPost(post.id, null, ctrl.signal);
        if (ctrl.signal.aborted) return;

        // The closet IS the type. Rehydrate from the post's first drawer so the
        // derived contentType (via closetFlow on its slug) reproduces the right
        // steps. If the drawer slug doesn't map to a special flow, fall back to
        // the post's own shape (auction → sales-room, event → q&a, redesign
        // format → redesign) so older posts still open with the right steps.
        const firstDrawer = post.drawers?.[0] ?? null;
        let pickDrawerId: string | null = firstDrawer?.id ?? null;
        if (firstDrawer && closetFlow(firstDrawer.slug) === 'post') {
          const wantSlug =
            post.format === 'redesign' ? 'redesign-challenge'
              : post.auction ? 'sales-room'
                : post.event ? 'monthly-zoom-qa'
                  : null;
          if (wantSlug) {
            const match = (post.drawers ?? []).find((d) => d.slug === wantSlug);
            if (match) pickDrawerId = match.id;
          }
        }
        setSelectedDrawerId(pickDrawerId);

        setTitle(post.title);
        setHideTitle(!!post.hide_title);
        setDek(post.dek ?? '');
        setSlug(post.slug);
        setSlugDirty(true);
        setBody(post.body_md ?? '');
        setButtons(post.buttons ?? []);

        // media_items (Media[]) → ready MediaEntry[]. images carry their url
        // (provider_id/thumbnail_url); videos carry their media row id.
        const entries: MediaEntry[] = (post.media_items ?? []).map((m, i) => {
          if (m.kind === 'video') {
            return {
              id: `edit_${post.id}_${i}`,
              kind: 'video',
              previewUrl: m.thumbnail_url ?? '',
              filename: 'Existing video',
              status: 'ready',
              progress: 1,
              mediaId: m.id,
              aspectRatio: m.aspect_ratio,
              // Round-trip the custom poster so re-saving the post keeps it.
              thumbUrl: m.thumbnail_url ?? null,
            };
          }
          const url = m.thumbnail_url ?? m.provider_id;
          return {
            id: `edit_${post.id}_${i}`,
            kind: 'image',
            previewUrl: url,
            filename: 'Existing image',
            status: 'ready',
            progress: 1,
            url,
            aspectRatio: m.aspect_ratio,
          };
        });
        setMedia(entries);

        if (pollOpts.length > 0) {
          setPollEnabled(true);
          setPollOptions(pollOpts.map((o) => o.label));
        }

        setResourceUrl(post.resource_url ?? '');
        setThumbnailUrl(post.thumbnail_url ?? '');

        if (post.auction) {
          setLotNumber(post.auction.lot_number);
          setLotName(post.auction.lot_name);
          setAuctionStarts(toLocalInput(new Date(post.auction.starts_at)));
          setAuctionEnds(toLocalInput(new Date(post.auction.ends_at)));
          setMinBid(post.auction.min_bid ? String(Math.round(post.auction.min_bid / 100)) : '');
          // Null means unset; 0 is a real value (free postage), so no truthy shortcut.
          setPostageCost(post.auction.shipping_cost == null ? '' : String(post.auction.shipping_cost / 100));
          setParcelPreset(post.auction.parcel_preset ?? '');
          setWeightGrams(post.auction.weight_grams == null ? '' : String(post.auction.weight_grams));
          setItemCategory(post.auction.item_category ?? '');
          setBuyNowPrice(post.auction.buy_now_price == null ? '' : String(post.auction.buy_now_price / 100));
          setShippingInfo(post.auction.shipping_info ?? '');
          setSizeInfo(post.auction.size_info ?? '');
          setMaterialInfo(post.auction.material_info ?? '');
        }

        if (post.event) {
          setEventStarts(toLocalInput(new Date(post.event.starts_at)));
          if (post.event.ends_at) {
            setHasEventEnd(true);
            setEventEnds(toLocalInput(new Date(post.event.ends_at)));
          }
          setEventUrl(post.event.location_url ?? '');
        }

        setEditPostId(post.id);
        setEditPublished(Boolean(post.published_at));
        setPrefilling(false);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[admin/edit] prefill failed', err);
        setPrefillError(err?.message ?? 'Could not load the post.');
        setPrefilling(false);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editSlug]);

  // Pre-fill the redesign trace from the chosen closet's default resource.
  useEffect(() => {
    if (isEditing) return; // keep the post's saved trace, don't auto-fill
    if (contentType !== 'redesign') return;
    if (!selectedDrawerId || resourceUrl) return;
    const ctrl = new AbortController();
    fetchDrawerDefaultResource(selectedDrawerId, ctrl.signal)
      .then((url) => { if (!ctrl.signal.aborted && url) setResourceUrl(url); })
      .catch((err) => { if (err?.name !== 'AbortError') console.error(err); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, selectedDrawerId]);

  // ---------- Step plan (depends on content type) ----------
  const steps: StepDef[] = useMemo(() => {
    const s: StepDef[] = [
      { key: 'type', label: 'Closet' },
      { key: 'details', label: 'Details' },
      { key: 'media', label: 'Media' },
    ];
    if (contentType === 'redesign') s.push({ key: 'redesign', label: 'Trace' });
    if (contentType === 'auction') s.push({ key: 'auction', label: 'Saleroom' });
    if (contentType === 'event') s.push({ key: 'event', label: 'Zoom Q+A' });
    s.push({ key: 'buttons', label: 'Buttons' });
    s.push({ key: 'review', label: 'Review' });
    return s;
  }, [contentType]);

  // Clamp position if the plan shrank (e.g. switched type on step 1).
  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length, stepIndex]);

  const currentKey = steps[stepIndex]?.key ?? 'type';

  // ---------- Autosave plumbing (create route only) ----------
  // A single serialized snapshot drives both the instant local backup and the
  // debounced server draft. Only ready media is persisted (pending uploads
  // can't survive a reload). Recomputed each render - cheap for this form.
  const localSnapshot: LocalDraftSnapshot = {
    postId: editPostId,
    selectedDrawerId,
    title, hideTitle, dek, slug, slugDirty, body,
    media: media.filter((m) => m.status === 'ready'),
    thumbnailUrl, resourceUrl, buttons,
    pollEnabled, pollOptions,
    eventStarts, hasEventEnd, eventEnds, eventUrl,
    lotNumber, lotName, auctionStarts, auctionEnds, minBid, postageCost, parcelPreset, weightGrams, itemCategory, buyNowPrice, shippingInfo, sizeInfo, materialInfo,
  };
  const snapshotJson = JSON.stringify(localSnapshot);

  // Restore an unsaved local snapshot once on mount (create route).
  useEffect(() => {
    if (isEditing || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = window.localStorage.getItem(LOCAL_DRAFT_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as LocalDraftSnapshot;
      if (!s || (!s.title && !s.postId)) return;
      setEditPostId(s.postId ?? null);
      setSelectedDrawerId(s.selectedDrawerId ?? null);
      setTitle(s.title ?? '');
      setHideTitle(Boolean(s.hideTitle));
      setDek(s.dek ?? '');
      setSlug(s.slug ?? '');
      setSlugDirty(Boolean(s.slugDirty));
      setBody(s.body ?? '');
      // Rebind ready image previews to their uploaded URL (old object URLs are dead).
      setMedia((s.media ?? []).map((m) =>
        m.kind === 'image' ? { ...m, previewUrl: m.url ?? m.previewUrl } : m));
      setThumbnailUrl(s.thumbnailUrl ?? '');
      setResourceUrl(s.resourceUrl ?? '');
      setButtons(s.buttons ?? []);
      setPollEnabled(Boolean(s.pollEnabled));
      setPollOptions(s.pollOptions ?? ['', '']);
      setEventStarts(s.eventStarts ?? isoLocalNextHour());
      setHasEventEnd(Boolean(s.hasEventEnd));
      setEventEnds(s.eventEnds ?? isoLocalPlus(60));
      setEventUrl(s.eventUrl ?? '');
      setLotNumber(s.lotNumber ?? '');
      setLotName(s.lotName ?? '');
      setAuctionStarts(s.auctionStarts ?? isoLocalPlus(0));
      setAuctionEnds(s.auctionEnds ?? isoLocalPlus(24 * 60));
      setMinBid(s.minBid ?? '');
      setPostageCost(s.postageCost ?? '');
      setParcelPreset(s.parcelPreset ?? '');
      setWeightGrams(s.weightGrams ?? '');
      setItemCategory(s.itemCategory ?? '');
      setBuyNowPrice(s.buyNowPrice ?? '');
      setShippingInfo(s.shippingInfo ?? '');
      setSizeInfo(s.sizeInfo ?? '');
      setMaterialInfo(s.materialInfo ?? '');
      if (s.postId) { setAutosave('saved'); }
      toast.success('Restored your unsaved draft.');
    } catch (err) {
      console.error('[admin/new] local restore failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Cross-tab safety: if another tab publishes/discards this draft it clears
  // the shared localStorage key. Detach so this tab stops re-writing the key
  // (which would resurrect a deleted draft) and stops autosaving the dead row.
  useEffect(() => {
    if (isEditing) return;
    function onStorage(e: StorageEvent) {
      if (e.key === LOCAL_DRAFT_KEY && e.newValue === null) {
        setDraftDetached(true);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isEditing]);

  // Instant local backup (debounced lightly). Skips the initial mount so a
  // fresh page doesn't clobber a stored snapshot before the restore runs.
  useEffect(() => {
    if (isEditing || draftDetached) return;
    if (skipNextLocalSave.current) { skipNextLocalSave.current = false; return; }
    const t = setTimeout(() => {
      try { window.localStorage.setItem(LOCAL_DRAFT_KEY, snapshotJson); } catch { /* quota */ }
    }, 500);
    return () => clearTimeout(t);
  }, [snapshotJson, isEditing, draftDetached]);

  // Debounced server draft write - mints/updates the real post row.
  useEffect(() => {
    if (isEditing || draftDetached || !title.trim()) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { void persistDraft(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotJson, isEditing, draftDetached]);

  // Warn before closing/refreshing while an upload is in flight - a large video
  // can take minutes and is NOT in the saved draft until it's done, so leaving
  // now would lose it. Must stay above the early returns below so the hook order
  // is stable across the edit route's prefill (loading → loaded) re-render.
  const mediaPending = media.some((m) => m.status === 'uploading');
  useEffect(() => {
    if (!mediaPending) return;
    function warn(e: BeforeUnloadEvent) { e.preventDefault(); e.returnValue = ''; }
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [mediaPending]);

  if (loading) return null;
  if (!profile || (profile.role !== 'admin' && profile.role !== 'developer')) {
    return <Navigate to="/" replace />;
  }

  if (isEditing && prefilling) {
    return (
      <div className="newpost">
        <div className="container container--md">
          <p className="newpost__sub" aria-busy="true">Loading post…</p>
        </div>
      </div>
    );
  }
  if (isEditing && prefillError) {
    return (
      <div className="newpost">
        <div className="container container--md">
          <Link to="/admin" className="newpost__back">← Back to admin</Link>
          <p className="newpost__msg newpost__msg--error">{prefillError}</p>
        </div>
      </div>
    );
  }

  // Poll add-on shows only for the standard Post type.
  const showsPoll = contentType === 'post';

  // ---------- Redesign trace upload ----------
  async function handleResourceFile(file: File | null) {
    if (!file || !profile) return;
    setResourceUploading(true);
    setErrorText('');
    try {
      const url = await uploadPostResource({ authorId: profile.id, file });
      setResourceUrl(url);
      toast.success('Trace resource uploaded.');
    } catch (err: any) {
      setErrorText(err?.message ?? 'Resource upload failed.');
      toast.error(err?.message ?? 'Resource upload failed.');
    } finally {
      setResourceUploading(false);
    }
  }

  // ---------- Poll helpers ----------
  function setPollOption(i: number, value: string) {
    setPollOptions((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  }
  function addPollOption() {
    setPollOptions((prev) => (prev.length >= MAX_POLL_OPTIONS ? prev : [...prev, '']));
  }
  function removePollOption(i: number) {
    setPollOptions((prev) => (prev.length <= MIN_POLL_OPTIONS ? prev : prev.filter((_, idx) => idx !== i)));
  }
  const trimmedPollOptions = pollOptions.map((s) => s.trim()).filter((s) => s.length > 0);
  const pollValid = !pollEnabled || trimmedPollOptions.length >= MIN_POLL_OPTIONS;

  // ---------- Per-step validation ----------
  const mediaReady = media.filter((m) => m.status === 'ready');
  const hasDescription = htmlToText(body).length > 0;

  const eventValid = (() => {
    if (!eventStarts) return false;
    if (hasEventEnd && new Date(eventEnds) <= new Date(eventStarts)) return false;
    if (!eventUrl.trim()) return false;
    try { new URL(eventUrl.trim()); } catch { return false; }
    return true;
  })();

  const minBidNum = parseInt(minBid, 10);
  const postageNum = parseFloat(postageCost);
  const minBidValid = Number.isFinite(minBidNum) && minBidNum >= 1;
  // Compare Buy Now against the starting bid in CENTS with the exact rounding
  // buildPayload applies (min_bid rounds to whole dollars, buy_now_price keeps
  // cents) so the wizard can never pass a pair insertAuction/upsertAuction
  // will reject at publish time.
  const minBidCents = Math.round(parseFloat(minBid) || 0) * 100;
  const buyNowCents = Math.round(parseFloat(buyNowPrice) * 100);
  // Both are optional; postage may be 0 (free), Buy Now must clear the starting
  // bid - but only check that once the starting bid itself is valid (an empty
  // starting bid is its own error, not Buy Now's).
  const postageValid = postageCost.trim() === '' || (Number.isFinite(postageNum) && postageNum >= 0);
  // Live postage (067) is a pair: preset + weight together or neither -
  // mirrors validateParcel in stream-queries so publish can't fail late.
  const weightNum = parseInt(weightGrams, 10);
  const parcelValid =
    (parcelPreset === '' && weightGrams.trim() === '') ||
    (parcelPreset !== '' && Number.isFinite(weightNum) && weightNum >= 1);
  const buyNowValid =
    buyNowPrice.trim() === '' ||
    (Number.isFinite(buyNowCents) && (!minBidValid || buyNowCents >= minBidCents));
  const auctionValid =
    lotNumber.trim().length > 0 &&
    lotName.trim().length > 0 &&
    !!auctionStarts && !!auctionEnds &&
    new Date(auctionEnds) > new Date(auctionStarts) &&
    minBidValid &&
    postageValid && parcelValid && buyNowValid;

  // Boundary (081): a lot can only be LISTED (published / scheduled) once it has
  // the shipping trio - parcel size + weight + item category - so a live carrier
  // rate AND a customs declaration can always be produced. Drafts may omit them.
  const auctionSellable =
    auctionValid && parcelPreset !== '' && Number.isFinite(weightNum) && weightNum >= 1 && itemCategory !== '';

  // A text post (no media) requires a non-empty description.
  const detailsValid =
    !!title.trim() &&
    !!slug.trim() &&
    pollValid &&
    (mediaReady.length > 0 || hasDescription);

  function stepValid(key: string): boolean {
    switch (key) {
      case 'type': return !!selectedDrawerId;
      case 'details': return detailsValid;
      case 'media':
        // Either at least one media item, or a valid text post (description).
        return !mediaPending && (mediaReady.length > 0 || hasDescription);
      case 'redesign': return true; // trace is optional (falls back to drawer default)
      case 'auction': return auctionValid;
      case 'event': return eventValid;
      case 'buttons': return true;
      case 'review': return true;
      default: return true;
    }
  }

  const canNext = stepValid(currentKey) && !publishing;
  const isLast = stepIndex === steps.length - 1;

  function goNext() {
    if (!canNext || isLast) return;
    const next = Math.min(stepIndex + 1, steps.length - 1);
    setStepIndex(next);
    setMaxReached((m) => Math.max(m, next));
    setErrorText('');
  }
  function goBack() {
    if (stepIndex === 0) return;
    setStepIndex(stepIndex - 1);
    setErrorText('');
  }
  // Tabs: jump to ANY step. Incomplete steps are flagged in the stepper and
  // the final publish stays gated on validity, so free movement is safe.
  function jumpTo(i: number) {
    setStepIndex(i);
    setMaxReached((m) => Math.max(m, i));
    setErrorText('');
  }

  // Steps that have been reached but aren't yet valid - surfaced as "!" in the
  // stepper so jumping around makes the gaps obvious.
  const invalidKeys = new Set(steps.filter((s) => !stepValid(s.key)).map((s) => s.key));

  function buildPayload(draft: boolean, publishAt?: string | null): NewWizardPostInput {
    const pollPayload = showsPoll && pollEnabled ? trimmedPollOptions : undefined;
    const eventPayload: NewEventInput | undefined = contentType === 'event' ? {
      starts_at: new Date(eventStarts).toISOString(),
      ends_at: hasEventEnd ? new Date(eventEnds).toISOString() : null,
      location_kind: 'online',
      location_url: eventUrl.trim(),
      location_address: null,
    } : undefined;
    const auctionPayload: NewAuctionInput | undefined = contentType === 'auction' ? {
      lot_number: lotNumber.trim(),
      lot_name: lotName.trim(),
      starts_at: new Date(auctionStarts).toISOString(),
      ends_at: new Date(auctionEnds).toISOString(),
      min_bid: Math.round(parseFloat(minBid) || 0) * 100,
      shipping_cost: postageCost.trim() === '' ? null : Math.round(parseFloat(postageCost) * 100),
      parcel_preset: parcelPreset === '' ? null : (parcelPreset as 'small' | 'medium' | 'large'),
      weight_grams: weightGrams.trim() === '' ? null : Math.round(parseInt(weightGrams, 10)),
      item_category: itemCategory === '' ? null : itemCategory,
      buy_now_price: buyNowPrice.trim() === '' ? null : Math.round(parseFloat(buyNowPrice) * 100),
      shipping_info: shippingInfo.trim() || null,
      size_info: sizeInfo.trim() || null,
      material_info: materialInfo.trim() || null,
    } : undefined;

    return {
      title: title.trim(),
      hide_title: hideTitle,
      slug: slug.trim(),
      dek: dek.trim() || null,
      body_md: hasDescription ? body : null,
      format: formatForFlow(contentType),
      drawer_ids: selectedDrawerId ? [selectedDrawerId] : [],
      media: toWizardMedia(media),
      buttons: cleanButtons(buttons),
      resource_url: contentType === 'redesign' ? (resourceUrl.trim() || null) : null,
      thumbnail_url: thumbnailUrl.trim() || null,
      poll_options: pollPayload,
      event: eventPayload,
      auction: auctionPayload,
      draft,
      publish_at: publishAt ?? null,
    };
  }

  // ---------- Thumbnail upload ----------
  async function handleThumbFile(file: File | null) {
    if (!file || !profile) return;
    setThumbUploading(true);
    setErrorText('');
    try {
      const url = await uploadPostCover({ authorId: profile.id, file });
      setThumbnailUrl(url);
      toast.success('Feed thumbnail uploaded.');
    } catch (err: any) {
      setErrorText(err?.message ?? 'Thumbnail upload failed.');
      toast.error(err?.message ?? 'Thumbnail upload failed.');
    } finally {
      setThumbUploading(false);
    }
  }

  // ---------- Autosave (create route) ----------
  function clearLocalDraft() {
    try { window.localStorage.removeItem(LOCAL_DRAFT_KEY); } catch { /* ignore */ }
  }

  /** Debounced server write that mints/updates the draft post row. Create route
   *  only; needs a title; coalesces overlapping writes via the in-flight ref. */
  async function persistDraft() {
    if (isEditing || draftDetached || !profile) return;
    if (!title.trim()) return;
    if (savingRef.current) { dirtyRef.current = true; return; }
    savingRef.current = true;
    setAutosave('saving');
    try {
      const payload = buildPayload(true);
      if (editPostId) {
        try {
          await adminUpdateWizardPost(editPostId, payload);
        } catch (e: any) {
          // The draft row was deleted out from under us (e.g. discarded in
          // another tab). Forget the dead id and mint a fresh draft instead of
          // looping on a 0-row update.
          if (e?.code === 'PGRST116') {
            setEditPostId(null);
            const res = await adminCreateWizardPost(profile.id, payload);
            setEditPostId(res.id);
          } else {
            throw e;
          }
        }
      } else {
        try {
          const res = await adminCreateWizardPost(profile.id, payload);
          setEditPostId(res.id);
        } catch (e: any) {
          // Slug already taken (another draft/post) - dedupe and retry once so
          // autosave doesn't get stuck. Keep the field in sync with the stored slug.
          if (e?.code === '23505') {
            const deduped = `${payload.slug}-${Date.now().toString(36).slice(-4)}`;
            const res = await adminCreateWizardPost(profile.id, { ...payload, slug: deduped });
            setEditPostId(res.id);
            setSlug(deduped);
            setSlugDirty(true);
          } else {
            throw e;
          }
        }
      }
      setAutosave('saved');
      setSavedAt(Date.now());
    } catch (err: any) {
      console.error('[admin/new] autosave failed', err);
      setAutosave('error');
    } finally {
      savingRef.current = false;
      if (dirtyRef.current) { dirtyRef.current = false; void persistDraft(); }
    }
  }

  /** Final action from the Review step (create route): save draft, publish now,
   *  or schedule for a future time. After autosave the row already exists, so
   *  this saves the latest fields then flips published_at via setPostSchedule. */
  async function finalizeCreate(mode: 'draft' | 'publish' | 'schedule', whenISO?: string) {
    if (!profile) return;
    if (mode !== 'draft') {
      if (!detailsValid) { setErrorText('Title, slug and either media or a description are required.'); setStatus('error'); return; }
      if (contentType === 'event' && !eventValid) { setErrorText('Zoom Q+A details are incomplete.'); setStatus('error'); return; }
      if (contentType === 'auction' && !auctionSellable) { setErrorText('To list a lot, set its parcel size, packed weight and item category - they drive live shipping and the customs label.'); setStatus('error'); return; }
    }
    if (mode === 'schedule' && (!whenISO || new Date(whenISO).getTime() <= Date.now())) {
      setErrorText('Pick a schedule time in the future.'); setStatus('error'); return;
    }
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    setStatus('publishing');
    setErrorText('');
    try {
      let targetSlug = slug.trim();
      if (editPostId) {
        targetSlug = await adminUpdateWizardPost(editPostId, buildPayload(true));
        if (mode === 'publish') await setPostSchedule(editPostId, new Date().toISOString());
        else if (mode === 'schedule') await setPostSchedule(editPostId, whenISO!);
        // draft: published_at already null
      } else {
        const publishAt = mode === 'schedule' ? whenISO! : mode === 'publish' ? new Date().toISOString() : null;
        const res = await adminCreateWizardPost(profile.id, buildPayload(mode === 'draft', publishAt));
        setEditPostId(res.id);
        targetSlug = res.slug;
      }
      clearLocalDraft();
      toast.success(mode === 'publish' ? 'Published.' : mode === 'schedule' ? 'Scheduled.' : 'Saved as draft.');
      navigate(mode === 'publish' ? `/p/${targetSlug}` : '/admin');
    } catch (err: any) {
      console.error('[admin/new] finalize failed', err);
      setErrorText(err?.message ?? 'Save failed. Check slug uniqueness + required fields.');
      toast.error(err?.message ?? 'Save failed.');
      setStatus('error');
    }
  }

  /** Discard the in-progress draft: delete the row (if minted) + clear local. */
  async function handleDiscard() {
    if (!window.confirm('Discard this draft? This permanently deletes it.')) return;
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    clearLocalDraft();
    if (editPostId && !isEditing) {
      try { await adminDeletePost(editPostId); }
      catch (err: any) { console.error('[admin/new] discard failed', err); }
    }
    navigate('/admin');
  }

  async function saveEdit() {
    if (!profile || !editPostId) return;
    if (!detailsValid) { setErrorText('Title and either media or a description are required.'); setStatus('error'); return; }
    if (contentType === 'event' && !eventValid) { setErrorText('Zoom Q+A details are incomplete.'); setStatus('error'); return; }
    if (contentType === 'auction' && !auctionValid) { setErrorText('Saleroom details are incomplete.'); setStatus('error'); return; }
    setStatus('publishing');
    setErrorText('');
    try {
      // `draft` is unused by the update (published state is preserved), but
      // buildPayload requires it; pass the post's current state for clarity.
      const savedSlug = await adminUpdateWizardPost(editPostId, buildPayload(!editPublished));
      toast.success('Changes saved.');
      navigate(`/p/${savedSlug}`);
    } catch (err: any) {
      console.error('[admin/edit] save failed', err);
      setErrorText(err?.message ?? 'Save failed. Check the required fields.');
      toast.error(err?.message ?? 'Save failed.');
      setStatus('error');
    }
  }

  // Header label: the chosen closet's name once picked, else a generic prompt.
  const headerLabel = selectedDrawer?.name ?? 'Post';

  return (
    <div className="newpost">
      <div className="container container--md">
        <header className="newpost__head">
          <Link to="/admin" className="newpost__back">← Back to admin</Link>
          <p className="kicker">{isEditing ? 'Edit post' : `New ${headerLabel}`}</p>
          <Heading level="h1">{isEditing ? 'Edit this post.' : 'Publish to the Club.'}</Heading>
          <Text role="lede" muted>
            {isEditing
              ? 'Update the post and save. Publishing stays in the admin list, so the live/draft state is preserved.'
              : 'Jump between steps freely: your work autosaves as a draft. Publish now or schedule it for later at the end.'}
          </Text>
        </header>

        <WizardStepper
          steps={steps}
          current={stepIndex}
          maxReached={maxReached}
          onJump={jumpTo}
          invalidKeys={invalidKeys}
        />

        {!isEditing && (
          <p className="newpost__autosave" aria-live="polite">
            {draftDetached
              ? '⚠ This draft was published or discarded in another tab, so autosave is paused here. Reload to start fresh.'
              : <>
                  {autosave === 'saving' && 'Saving draft…'}
                  {autosave === 'saved' && `Draft autosaved${savedAt ? ' · ' + new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}`}
                  {autosave === 'error' && '⚠ Autosave failed. Your work is kept locally. It will retry as you edit.'}
                  {autosave === 'idle' && 'Drafts autosave as you type. Find them under Content in admin.'}
                </>}
          </p>
        )}

        <div className="newpost__form newpost__wizard">
          {/* ===== Step: Closet ===== */}
          {currentKey === 'type' && (
            <div className="newpost__field">
              <label>Which closet is this for?</label>
              <p className="newpost__hint">
                The closet sets the post's behaviour: the Saleroom opens an
                auction, the Redesign Challenge adds a trace + submissions, the
                Zoom Q+A schedules the call. Any other closet is a standard post.
              </p>
              <div className="newpost__type-cards">
                {drawers.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={`newpost__type-card ${selectedDrawerId === d.id ? 'newpost__type-card--on' : ''}`}
                    onClick={() => setSelectedDrawerId(d.id)}
                    aria-pressed={selectedDrawerId === d.id}
                  >
                    <span className="newpost__type-card-name">{d.name}</span>
                    {d.description && (
                      <span className="newpost__type-card-blurb">{d.description}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ===== Step: Details ===== */}
          {currentKey === 'details' && (
            <div className="newpost__details">
              <section className="newpost__group">
                <div className="newpost__row">
                  <div className="newpost__field">
                    <label htmlFor="np-title">Title</label>
                    <Input
                      id="np-title"
                      type="text"
                      required
                      maxLength={200}
                      placeholder="A silk-blouse re-cut, start to finish"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={publishing}
                    />
                  </div>

                  <div className="newpost__field">
                    <label htmlFor="np-slug">URL slug</label>
                    <Input
                      id="np-slug"
                      type="text"
                      required
                      maxLength={80}
                      value={slug}
                      onChange={(e) => { setSlug(slugify(e.target.value)); setSlugDirty(true); }}
                      disabled={publishing || isEditing}
                      readOnly={isEditing}
                    />
                    <p className="newpost__hint">
                      /p/{slug || '…'}{isEditing && ' (locked, the URL can\'t change)'}
                    </p>
                  </div>
                </div>

                {/* Full-width below the title/slug row so it never crowds the
                    Description below it (and keeps the row's two inputs clean). */}
                <label className="newpost__checkline">
                  <input
                    type="checkbox"
                    checked={hideTitle}
                    onChange={(e) => setHideTitle(e.target.checked)}
                    disabled={publishing}
                  />
                  <span>Hide the title on the post &amp; feed card (when the cover/first frame already says it)</span>
                </label>

                <div className="newpost__field">
                  <label htmlFor="np-dek">Subheading <span className="newpost__optional">(optional)</span></label>
                  <Input
                    id="np-dek"
                    type="text"
                    maxLength={200}
                    placeholder="A short line that sits between the title and the description"
                    value={dek}
                    onChange={(e) => setDek(e.target.value)}
                    disabled={publishing}
                  />
                </div>
              </section>

              <section className="newpost__group">
                <div className="newpost__field">
                  <label htmlFor="np-body">Description</label>
                  <RichTextEditor
                    value={body}
                    onChange={setBody}
                    disabled={publishing}
                    placeholder="Write the post description. Required for a text-only post (no media)."
                  />
                </div>
              </section>

              {showsPoll && (
                <section className="newpost__group">
                  <div className="newpost__field newpost__poll">
                    <label className="newpost__poll-toggle">
                      <input
                        type="checkbox"
                        checked={pollEnabled}
                        onChange={(e) => setPollEnabled(e.target.checked)}
                        disabled={publishing}
                      />
                      <span>Add a poll</span>
                    </label>
                    {pollEnabled && (
                      <div className="newpost__poll-body">
                        <p className="newpost__hint">
                          Members can tick any combination of options once the post is live.
                        </p>
                        {pollOptions.map((opt, i) => (
                          <div key={i} className="newpost__poll-row">
                            <Input
                              type="text"
                              maxLength={120}
                              placeholder={`Option ${i + 1}`}
                              value={opt}
                              onChange={(e) => setPollOption(i, e.target.value)}
                              disabled={publishing}
                            />
                            {pollOptions.length > MIN_POLL_OPTIONS && (
                              <button
                                type="button"
                                className="newpost__poll-remove"
                                onClick={() => removePollOption(i)}
                                aria-label={`Remove option ${i + 1}`}
                                disabled={publishing}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        {pollOptions.length < MAX_POLL_OPTIONS && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="newpost__poll-add"
                            onClick={addPollOption}
                            disabled={publishing}
                          >
                            + Add option
                          </Button>
                        )}
                        {!pollValid && (
                          <p className="newpost__msg newpost__msg--error">
                            A poll needs at least {MIN_POLL_OPTIONS} non-empty options.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ===== Step: Media ===== */}
          {currentKey === 'media' && (
            <div className="newpost__media-step">
              <div className="newpost__field">
                <label>Media</label>
                <p className="newpost__hint">
                  {contentType === 'event'
                    ? 'Optional: attach the call recording or a thumbnail. The first item is the cover. Leave empty if the recording isn\'t ready yet.'
                    : 'Add any number of images and videos (up to 20), in any order. The first item is the cover. Leave empty for a text-only post (needs a description on the Details step).'}
                </p>
                <MediaUploader
                  authorId={profile.id}
                  items={media}
                  onChange={setMedia}
                  disabled={publishing}
                  onStreamNotConfigured={() => setStreamNotConfigured(true)}
                  max={20}
                />
                {mediaReady.some((m) => m.kind === 'video') && (
                  <p className="newpost__hint">
                    Cloudflare is processing any videos. They'll be ready in ~1 minute;
                    you can publish now and they'll play once ready.
                  </p>
                )}
                {streamNotConfigured && (
                  <p className="newpost__msg newpost__msg--error">
                    Cloudflare Stream isn't configured on this deploy. Set
                    <code> CLOUDFLARE_ACCOUNT_ID</code> and
                    <code> CLOUDFLARE_STREAM_API_TOKEN</code> in the Pages env,
                    then redeploy. Images still upload fine.
                  </p>
                )}
              </div>

              <div className="newpost__field">
                <label>Feed thumbnail (optional)</label>
                <p className="newpost__hint">
                  The image shown on the closet wall and feed cards to represent
                  this post. Falls back to the first media item if left empty.
                </p>
                <div className="newpost__thumb">
                  <UploadZone
                    accept="image/*"
                    onFile={(f) => void handleThumbFile(f)}
                    progress={thumbUploading ? 0.5 : null}
                    done={Boolean(thumbnailUrl) && !thumbUploading}
                    filename={thumbnailUrl ? 'Current thumbnail' : null}
                    label="Drop a thumbnail image"
                    hint="A square or 4:5 image works best."
                    onClear={thumbnailUrl ? () => setThumbnailUrl('') : undefined}
                    disabled={publishing}
                  />
                  {thumbnailUrl && (
                    <img className="newpost__thumb-preview" src={thumbnailUrl} alt="" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== Step: Redesign trace ===== */}
          {currentKey === 'redesign' && (
            <div className="newpost__field">
              <label>Trace resource (downloadable)</label>
              <p className="newpost__hint">
                The PDF members download to trace this challenge. Pre-filled from the
                chosen closet's default; saving a different file overrides the closet
                default going forward.
              </p>
              <UploadZone
                accept="application/pdf,image/*"
                onFile={(f) => void handleResourceFile(f)}
                progress={resourceUploading ? 0.5 : null}
                done={Boolean(resourceUrl) && !resourceUploading}
                filename={resourceUrl ? 'Current trace resource' : null}
                label="Drop a trace PDF"
                hint="PDF preferred. Optional, falls back to the closet default."
                onClear={resourceUrl ? () => setResourceUrl('') : undefined}
                disabled={publishing}
              />
              {resourceUrl && (
                <p className="newpost__hint">
                  <a href={resourceUrl} target="_blank" rel="noreferrer">Preview current resource ↗</a>
                </p>
              )}
            </div>
          )}

          {/* ===== Step: Saleroom (auction) ===== */}
          {currentKey === 'auction' && (
            <div className="newpost__auction-body">
              <p className="newpost__hint">
                USD bidding. Members bid exactly what they put down, and the highest bid wins. Bidding opens at your minimum bid, and the lot auto-extends 2 minutes if a bid lands in the final 2 minutes.
              </p>
              <div className="newpost__field">
                <label htmlFor="np-lot-number">LOT number</label>
                <Input
                  id="np-lot-number"
                  type="text"
                  maxLength={32}
                  placeholder="KH-001"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  disabled={publishing}
                  required
                />
              </div>
              <div className="newpost__field">
                <label htmlFor="np-lot-name">LOT name</label>
                <Input
                  id="np-lot-name"
                  type="text"
                  maxLength={120}
                  placeholder="Reworked silk shirt"
                  value={lotName}
                  onChange={(e) => setLotName(e.target.value)}
                  disabled={publishing}
                  required
                />
              </div>
              <div className="newpost__field">
                <label htmlFor="np-min-bid">Minimum bid (USD)</label>
                <Input
                  id="np-min-bid"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  placeholder="25"
                  value={minBid}
                  onChange={(e) => setMinBid(e.target.value)}
                  disabled={publishing}
                  required
                />
                <p className="newpost__hint">Bidding starts here: the first bid must be at least this amount.</p>
              </div>
              <div className="newpost__field">
                <label htmlFor="np-buy-now">Buy now price (USD, optional)</label>
                <Input
                  id="np-buy-now"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  placeholder="120"
                  value={buyNowPrice}
                  onChange={(e) => setBuyNowPrice(e.target.value)}
                  disabled={publishing}
                />
                <p className="newpost__hint">Lets a member end the auction instantly at this price - available until live bidding reaches it. Tip: set it at least 30% above the starting bid.</p>
                {buyNowPrice.trim() !== '' && !buyNowValid && (
                  <p className="newpost__msg newpost__msg--error">Buy now must be at least the starting bid.</p>
                )}
              </div>
              <div className="newpost__auction-row newpost__auction-times">
                <div className="newpost__field">
                  <label htmlFor="np-auction-starts">Opens at</label>
                  <Input
                    id="np-auction-starts"
                    type="datetime-local"
                    value={auctionStarts}
                    onChange={(e) => setAuctionStarts(e.target.value)}
                    disabled={publishing}
                    required
                  />
                </div>
                <div className="newpost__field">
                  <label htmlFor="np-auction-ends">Closes at</label>
                  <Input
                    id="np-auction-ends"
                    type="datetime-local"
                    value={auctionEnds}
                    onChange={(e) => setAuctionEnds(e.target.value)}
                    disabled={publishing}
                    required
                  />
                </div>
              </div>
              <div className="newpost__field">
                <label htmlFor="np-shipping">Shipping info (optional)</label>
                <Textarea
                  id="np-shipping"
                  rows={3}
                  maxLength={1000}
                  placeholder="Where you ship, who pays, etc."
                  value={shippingInfo}
                  onChange={(e) => setShippingInfo(e.target.value)}
                  disabled={publishing}
                />
              </div>
              <div className="newpost__field">
                <label htmlFor="np-postage">Postage (USD, optional)</label>
                <Input
                  id="np-postage"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.01}
                  placeholder="15"
                  value={postageCost}
                  onChange={(e) => setPostageCost(e.target.value)}
                  disabled={publishing}
                />
                <p className="newpost__hint">Flat postage shown to bidders next to the price. Enter 0 for free postage; leave empty to not show postage.</p>
              </div>
              <div className="newpost__auction-row">
                <div className="newpost__field">
                  <label htmlFor="np-parcel">Parcel size (optional)</label>
                  <select
                    id="np-parcel"
                    value={parcelPreset}
                    onChange={(e) => setParcelPreset(e.target.value)}
                    disabled={publishing}
                  >
                    <option value="">Not set - use flat postage</option>
                    <option value="small">Small - satchel / flat garment</option>
                    <option value="medium">Medium - standard box</option>
                    <option value="large">Large - bulky / multi-item</option>
                  </select>
                </div>
                <div className="newpost__field">
                  <label htmlFor="np-weight">Packed weight (grams)</label>
                  <input
                    id="np-weight"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="800"
                    value={weightGrams}
                    onChange={(e) => setWeightGrams(e.target.value)}
                    disabled={publishing}
                  />
                </div>
              </div>
              <div className="newpost__field">
                <label htmlFor="np-category">Item category</label>
                <select
                  id="np-category"
                  value={itemCategory}
                  onChange={(e) => setItemCategory(e.target.value)}
                  disabled={publishing}
                >
                  <option value="">Choose a category…</option>
                  <option value="dress">Dress</option>
                  <option value="top">Top / blouse</option>
                  <option value="bottoms">Trousers / skirt</option>
                  <option value="outerwear">Coat / jacket</option>
                  <option value="knitwear">Knitwear</option>
                  <option value="shoes">Shoes</option>
                  <option value="bag">Bag</option>
                  <option value="accessory">Accessory</option>
                  <option value="jewellery">Jewellery</option>
                  <option value="other">Other fashion item</option>
                </select>
              </div>
              <p className="newpost__hint">
                Parcel size, weight and item category are <strong>required to publish a lot</strong> - they
                drive the live carrier rate bidders see and the customs label on international parcels.
                Bidders get an estimate for their own address; the winner is charged the exact rate,
                confirmed at charge time.
              </p>
              {!parcelValid && (
                <p className="newpost__msg newpost__msg--error">
                  Parcel size and weight go together - set a size with a weight of at least 1 gram.
                </p>
              )}
              <div className="newpost__field">
                <label htmlFor="np-material">Material (optional)</label>
                <Textarea
                  id="np-material"
                  rows={3}
                  maxLength={1000}
                  placeholder="Fabric / composition, care instructions, etc."
                  value={materialInfo}
                  onChange={(e) => setMaterialInfo(e.target.value)}
                  disabled={publishing}
                />
              </div>
              <div className="newpost__field">
                <label htmlFor="np-size">Size + measurements (optional)</label>
                <Textarea
                  id="np-size"
                  rows={3}
                  maxLength={1000}
                  value={sizeInfo}
                  onChange={(e) => setSizeInfo(e.target.value)}
                  disabled={publishing}
                />
              </div>
              {!auctionValid && (
                <p className="newpost__msg newpost__msg--error">
                  LOT number, name, a minimum bid of at least $1, and a close time after the open time are required.
                </p>
              )}
            </div>
          )}

          {/* ===== Step: Zoom Q+A (event) ===== */}
          {currentKey === 'event' && (
            <div className="newpost__event-body">
              <p className="newpost__hint">
                The monthly members Zoom call. Always online, so members can RSVP and add it to their calendar.
              </p>

              <div className="newpost__event-row">
                <div className="newpost__field">
                  <label htmlFor="np-event-starts">Starts</label>
                  <Input
                    id="np-event-starts"
                    type="datetime-local"
                    value={eventStarts}
                    onChange={(e) => setEventStarts(e.target.value)}
                    disabled={publishing}
                    required
                  />
                </div>
              </div>

              <label className="newpost__event-end-toggle">
                <input
                  type="checkbox"
                  checked={hasEventEnd}
                  onChange={(e) => setHasEventEnd(e.target.checked)}
                  disabled={publishing}
                />
                <span>Has an end time</span>
              </label>
              {hasEventEnd && (
                <div className="newpost__event-row">
                  <div className="newpost__field">
                    <label htmlFor="np-event-ends">Ends</label>
                    <Input
                      id="np-event-ends"
                      type="datetime-local"
                      value={eventEnds}
                      onChange={(e) => setEventEnds(e.target.value)}
                      disabled={publishing}
                    />
                  </div>
                </div>
              )}

              <div className="newpost__field">
                <label htmlFor="np-event-url">Zoom link</label>
                <Input
                  id="np-event-url"
                  type="url"
                  placeholder="https://zoom.us/j/…"
                  value={eventUrl}
                  onChange={(e) => setEventUrl(e.target.value)}
                  disabled={publishing}
                  required
                />
              </div>

              {!eventValid && (
                <p className="newpost__msg newpost__msg--error">
                  A valid Zoom link is required, and the end time must be after the start.
                </p>
              )}
            </div>
          )}

          {/* ===== Step: Buttons ===== */}
          {currentKey === 'buttons' && (
            <div className="newpost__field">
              <label>Link buttons (optional)</label>
              <p className="newpost__hint">
                Calls-to-action shown on the post. Pick a style, give it a label and a URL.
              </p>
              <ButtonEditor buttons={buttons} onChange={setButtons} disabled={publishing} />
            </div>
          )}

          {/* ===== Step: Review ===== */}
          {currentKey === 'review' && (
            <div className="newpost__review">
              {/* Lead with the real post identity (closet -> title -> slug) so
                  the review reads as "here's the post", not a debug dump. */}
              <div className="newpost__review-lead">
                <Text role="eyebrow" muted>{selectedDrawer?.name ?? 'No closet'}</Text>
                <Heading level="h3">{title.trim() || 'Untitled post'}</Heading>
                <Text role="caption" muted>/p/{slug.trim() || '…'}</Text>
              </div>
              <dl className="newpost__review-list">
                <div><dt>Description</dt><dd>{hasDescription ? 'Set' : <em>none</em>}</dd></div>
                <div>
                  <dt>Media</dt>
                  <dd>
                    {mediaReady.length === 0 ? <em>text post</em> :
                      `${mediaReady.length} item${mediaReady.length === 1 ? '' : 's'} (${mediaReady.filter((m) => m.kind === 'image').length} image, ${mediaReady.filter((m) => m.kind === 'video').length} video), cover: ${mediaReady[0].kind}`}
                  </dd>
                </div>
                {showsPoll && pollEnabled && (
                  <div><dt>Poll</dt><dd>{trimmedPollOptions.length} options</dd></div>
                )}
                {contentType === 'redesign' && (
                  <div><dt>Trace</dt><dd>{resourceUrl ? 'set' : <em>closet default</em>}</dd></div>
                )}
                {contentType === 'auction' && (
                  <div><dt>Saleroom</dt><dd>LOT {lotNumber.trim()} - {lotName.trim()}</dd></div>
                )}
                {contentType === 'event' && (
                  <div><dt>Zoom Q+A</dt><dd>{new Date(eventStarts).toLocaleString()} - Online</dd></div>
                )}
                <div><dt>Thumbnail</dt><dd>{thumbnailUrl ? 'custom' : <em>first media</em>}</dd></div>
                <div><dt>Buttons</dt><dd>{cleanButtons(buttons).length || <em>none</em>}</dd></div>
              </dl>

              {mediaPending && (
                <p className="newpost__msg newpost__msg--error">
                  Some media is still uploading. Wait for it to finish before publishing.
                </p>
              )}

              {!isEditing && (
                <div className="newpost__schedule">
                  <label className="newpost__schedule-toggle">
                    <input
                      type="checkbox"
                      checked={scheduleOpen}
                      onChange={(e) => setScheduleOpen(e.target.checked)}
                      disabled={publishing}
                    />
                    <span>Schedule for later instead of publishing now</span>
                  </label>
                  {scheduleOpen && (
                    <div className="newpost__field">
                      <label htmlFor="np-schedule">Go live at</label>
                      <Input
                        id="np-schedule"
                        type="datetime-local"
                        value={scheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                        disabled={publishing}
                      />
                      <p className="newpost__hint">
                        The post stays hidden from members until this time, then appears automatically.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {errorText && (
            <p className={`newpost__msg ${status === 'error' ? 'newpost__msg--error' : ''}`}>{errorText}</p>
          )}

          {/* ===== Nav ===== */}
          <div className="newpost__wizard-nav">
            <div className="newpost__wizard-nav-left">
              {stepIndex === 0 ? (
                <Button as={Link} to="/admin" variant="ghost" size="md">Cancel</Button>
              ) : (
                <Button type="button" variant="ghost" size="md" onClick={goBack} disabled={publishing}>
                  ← Back
                </Button>
              )}
              {!isEditing && editPostId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="newpost__discard"
                  onClick={() => void handleDiscard()}
                  disabled={publishing}
                >
                  Discard draft
                </Button>
              )}
            </div>
            <div className="newpost__wizard-nav-right">
              {isLast && isEditing ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={() => void saveEdit()}
                  disabled={publishing || mediaPending || !detailsValid}
                >
                  {publishing ? 'Saving…' : 'Save changes'}
                </Button>
              ) : isLast ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    onClick={() => void finalizeCreate('draft')}
                    disabled={publishing || mediaPending}
                  >
                    {publishing ? 'Saving…' : 'Save draft'}
                  </Button>
                  {scheduleOpen ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="md"
                      onClick={() => void finalizeCreate('schedule', scheduleAt ? new Date(scheduleAt).toISOString() : undefined)}
                      disabled={publishing || mediaPending || !detailsValid}
                    >
                      {publishing ? 'Scheduling…' : 'Schedule'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      size="md"
                      onClick={() => void finalizeCreate('publish')}
                      disabled={publishing || mediaPending || !detailsValid}
                    >
                      {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={goNext}
                  disabled={!canNext}
                >
                  Next →
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
